import { OAuthProvider, signInWithRedirect, getRedirectResult, User, getAuth } from 'firebase/auth';
import { auth } from '@/firebase/config';
import { Organization } from '@/firebase/organizationService';

/**
 * Get Firebase auth handler URL for redirect
 * Note: We use a generic URL without tenantId parameter since we set tenantId on auth instance
 * Firebase will use the tenantId from the auth instance state during the redirect flow
 */
export const getFirebaseAuthHandlerUrl = (): string => {
  // Get Firebase project ID from auth domain
  const authDomain = auth?.app?.options?.authDomain;
  if (!authDomain) {
    throw new Error('Firebase auth domain not configured');
  }
  
  // Extract project ID from auth domain (e.g., "lemur-476302.firebaseapp.com" -> "lemur-476302")
  const projectId = authDomain.split('.')[0];
  
  // Use generic handler URL - Firebase will use tenantId from auth instance
  return `https://${projectId}.firebaseapp.com/__/auth/handler`;
};

/**
 * Get tenant-specific auth instance
 * Note: Firebase Auth supports tenant-based authentication by setting tenantId on auth instance
 */
const getTenantAuth = (tenantId: string) => {
  if (!auth) {
    throw new Error('Firebase Auth not initialized');
  }
  
  // For tenant-based auth, we need to set tenantId on the auth instance
  // Set tenant ID - this tells Firebase Auth to use the specified tenant
  // Note: We're modifying the existing auth instance, which is safe for tenant-based auth
  (auth as any).tenantId = tenantId;
  return auth;
};

/**
 * Initiate SSO login using OIDC provider configured in Identity Platform tenant
 * This works uniformly across all IdPs (Microsoft, Okta, Google Workspace, etc.)
 */
export const initiateSSOLogin = async (organization: Organization): Promise<void> => {
  try {
    if (!organization.sso?.enabled || !organization.sso.identityPlatformTenantId) {
      throw new Error('SSO not configured for this organization. Identity Platform tenant ID is required.');
    }

    if (!organization.sso.oidcProviderId) {
      throw new Error('SSO not configured for this organization. OIDC provider ID is required.');
    }

    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }

    // Get tenant-specific auth - this uses the tenant where OIDC provider is configured
    const identityPlatformTenantId = organization.sso.identityPlatformTenantId;
    const authInstance = getTenantAuth(identityPlatformTenantId);

    // Create OAuth provider using OIDC provider ID
    // The OIDC provider (issuer, client ID, secret, etc.) is configured in the Identity Platform tenant
    // Strip any existing "oidc." prefix to avoid double-prefixing
    const oidcProviderId = organization.sso.oidcProviderId?.startsWith('oidc.') 
      ? organization.sso.oidcProviderId 
      : `oidc.${organization.sso.oidcProviderId}`;
    const provider = new OAuthProvider(oidcProviderId);
    
    // Add standard OIDC scopes
    provider.addScope('email');
    provider.addScope('profile');
    provider.addScope('openid');

    // Store organization ID and tenant ID in session for callback
    sessionStorage.setItem('sso_org_id', organization.id || '');
    sessionStorage.setItem('sso_tenant_id', identityPlatformTenantId);

    console.log('🔐 Initiating SSO login with tenant:', identityPlatformTenantId);
    console.log('🔐 Using OIDC provider:', organization.sso.oidcProviderId);
    console.log('🔐 Redirect URL will be:', getFirebaseAuthHandlerUrl());
    console.log('ℹ️ Tenant ID is set on auth instance and stored in sessionStorage');

    // Initiate redirect sign-in
    await signInWithRedirect(authInstance, provider);
  } catch (error: any) {
    console.error('SSO login error:', error);
    console.error('Error code:', error.code);
    console.error('Error message:', error.message);
    console.error('Full error:', JSON.stringify(error, null, 2));
    
    // Provide more helpful error messages
    if (error.code === 'auth/unauthorized-domain') {
      throw new Error('Domain not authorized. Please add your domain to Firebase Auth authorized domains.');
    } else if (error.code === 'auth/operation-not-allowed') {
      throw new Error('OIDC provider not enabled in Identity Platform tenant. Please enable it in Google Cloud Console.');
    } else if (error.message?.includes('TENANT_NOT_FOUND') || error.message?.includes('tenant')) {
      const tenantId = organization.sso?.identityPlatformTenantId || 'unknown';
      throw new Error(`Tenant '${tenantId}' not found. Please verify the tenant ID in Google Cloud Identity Platform.`);
    } else if (error.message?.includes('PROVIDER_NOT_FOUND') || error.message?.includes('provider')) {
      const providerId = organization.sso?.oidcProviderId || 'unknown';
      throw new Error(`OIDC provider '${providerId}' not found in Identity Platform tenant. Please verify the provider ID.`);
    }
    
    throw new Error(`SSO login failed: ${error.message || error.code || 'Unknown error'}`);
  }
};

/**
 * Handle SSO callback and get authentication result from Firebase
 */
export const handleSSOCallback = async (): Promise<User | null> => {
  try {
    if (!auth) {
      throw new Error('Firebase Auth not initialized');
    }

    // Check if we have a tenant ID in session
    const tenantId = sessionStorage.getItem('sso_tenant_id');
    const authInstance = tenantId ? getTenantAuth(tenantId) : auth;

    console.log('🔄 Handling SSO callback with tenant:', tenantId || 'default');

    // Get the redirect result (can only be called once per redirect)
    const result = await getRedirectResult(authInstance);
    
    if (result && result.user) {
      console.log('✅ SSO authentication successful (from redirect result):', result.user.email);
      return result.user;
    }

    // If no redirect result but user is already authenticated, 
    // it means the redirect was already processed (possibly by another component)
    // Check if current user exists and matches our SSO session
    const currentUser = authInstance.currentUser;
    if (currentUser) {
      const ssoOrgId = sessionStorage.getItem('sso_org_id');
      if (ssoOrgId) {
        console.log('✅ User already authenticated (redirect already processed):', currentUser.email);
        return currentUser;
      }
    }

    console.log('⚠️ No redirect result found and no authenticated user');
    return null;
  } catch (error: any) {
    console.error('SSO callback error:', error);
    throw new Error(`SSO callback failed: ${error.message}`);
  }
};

/**
 * Extract user information from Firebase User
 */
export const extractUserFromSSO = (firebaseUser: User): {
  email: string;
  name: string;
  picture?: string;
} => {
  const email = firebaseUser.email || '';
  const name = firebaseUser.displayName || email.split('@')[0];
  const picture = firebaseUser.photoURL || undefined;

  return {
    email,
    name,
    picture,
  };
};

